import { memo, useCallback, useEffect, useMemo } from "react";
import { TicketChannelField } from "./TicketChannelField";
import { TicketChannelStepField } from "./TicketChannelStepField";
import TicketDefaultDocField from "./TicketDefaultDocField";
import { TicketEvent } from "./TicketEvent";
import { Box, Grid, Typography } from "@mui/material";
import { AutoComplete, ISelectType, useFormContext } from "@4uhub/lib4uhub";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { TTicketServiceAreaForm } from "../TicketServiceAreaSchema";
import TicketAutomationReceivedRuleField from "./TicketAutomationReceivedRuleField";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { ExternalChatField } from "./ExternalChatField";

const translationPath = "chatBot.page.service.ticketServiceArea.";

interface IActionSelectorProps {
  options: Array<{
    id: string;
    name: string;
    code: string;
    actionName: string;
  }>;
}

export const ActionSelector = memo(({ options }: IActionSelectorProps) => {
  const { t } = useTranslation();

  const { control, setValue } = useFormContext<TTicketServiceAreaForm>();

  const actionSelector = useWatch({ control, name: "actionSelector" });

  const cleanActions = useCallback(() => {
    setValue("ticketEventType", null);
    setValue("ticketEvent", null);
    setValue("ticketDefaultDoc", null);
    setValue("ticketChannel", null);
    setValue("ticketChannelStep", null);
    setValue("ticketAutomationReceivedRule", null);
    setValue("externalChatSiteApp", null);
    setValue("fullScreenMode", false);
    setValue("sendProtocolNumber", false);
  }, [setValue]);

  return (
    <>
      <Grid item xs={12}>
        <AutoComplete
          name="actionSelector"
          label={t(translationPath + "action_type")}
          size="small"
          onValueChange={cleanActions}
          options={options}
          getOptionLabel={(option) => {
            return option.name;
          }}
        />
      </Grid>

      {actionSelector?.code === "1" && <TicketEvent />}
      {actionSelector?.code === "2" && <TicketDefaultDocField />}
      {actionSelector?.code === "3" && (
        <>
          <TicketChannelField />
          <TicketChannelStepField />
        </>
      )}
      {actionSelector?.code === "4" && (
        <>
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: "#FEFF99",
                color: (t) => t.palette.warning.dark,
                p: 1.5,
                borderRadius: 1,
                border: (t) => `1px solid ${t.palette.warning.main}`,
                width: "fit-content",
              }}
            >
              <WarningAmberIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {t(translationPath + "received_rule_caption")}
              </Typography>
            </Box>
          </Grid>

          <TicketAutomationReceivedRuleField />
        </>
      )}
      {actionSelector?.code === "5" && <ExternalChatField />}
    </>
  );
});
